/**
 * How Stork Rentals Works Section — Home Page
 *
 * The three booking steps, then the two rental types available:
 * Short-Term (fixed dates) and Flexible Rolling (monthly, open-ended).
 * CMS-ready: heading/subheading/rental types come from the Backoffice when
 * configured and fall back to defaults. The steps are static.
 */

import { usePageSection } from '@/hooks/usePageContent';

interface RentalTypeCard {
    title: string;
    description: string;
    detail: string;
}

export interface HowItWorksContent {
    heading: string;
    subheading: string;
    rentalTypes: RentalTypeCard[];
}

const DEFAULT_HOW_IT_WORKS: HowItWorksContent = {
    heading: 'How Stork Rentals Works',
    subheading: 'Two ways to rent. Pick whichever fits your plans.',
    rentalTypes: [
        {
            title: 'Short-Term Rentals',
            description:
                'Fixed pickup and return dates. Made for holidays, visiting family, or any stay where you already know how long you need the gear.',
            detail: 'From 1 day up to a month. You pay for the whole rental at checkout.',
        },
        {
            title: 'Flexible, Rolling Rentals',
            description:
                'For anything longer than a month, or when you don\'t know the end date yet. You only need a start date.',
            detail: 'A set monthly charge rolls on until you return the item. No contract, cancel any time.',
        },
    ],
};

/** The booking flow in three steps. Static: it's the same whatever the CMS says about rental types. */
const BOOKING_STEPS = [
    {
        title: 'Pick a location and dates',
        body: 'Choose where you\'ll collect. Add dates now, or leave them open for a monthly rental.',
    },
    {
        title: 'Choose your gear',
        body: 'Browse by category, age or brand. Prices show as you go.',
    },
    {
        title: 'Collect it when you arrive',
        body: 'Your gear is ready at the pickup point. Bring it back at the end, and that\'s it.',
    },
] as const;

export const HowItWorksSection = () => {
    const cms = usePageSection<HowItWorksContent>('home', 'how-it-works');
    const content = cms ?? DEFAULT_HOW_IT_WORKS;

    return (
        <section
            aria-labelledby="how-it-works-heading"
            className="bg-[linear-gradient(180deg,rgba(246,246,233,1)_0%,rgba(255,255,255,1)_100%)] py-16 sm:py-24"
        >
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">

                {/* Section heading */}
                <div className="mb-10 sm:mb-12">
                    <h2
                        id="how-it-works-heading"
                        className="font-display text-4xl leading-[0.96] tracking-tight text-foreground sm:text-5xl"
                    >
                        {content.heading}
                    </h2>
                    <p className="mt-3 text-base leading-7 text-muted-foreground sm:text-lg">
                        {content.subheading}
                    </p>
                </div>

                {/* Booking steps: a real sequence, so the numbers carry meaning. */}
                <ol
                    aria-label="How to book"
                    className="mb-12 grid grid-cols-1 gap-6 border-y border-border/80 py-6 sm:grid-cols-3 sm:gap-8 sm:py-8"
                >
                    {BOOKING_STEPS.map((step, index) => (
                        <li key={step.title} className="flex items-start gap-4">
                            <span
                                className="font-display text-3xl leading-none text-primary"
                                aria-hidden="true"
                            >
                                {index + 1}
                            </span>
                            <div>
                                <p className="text-base font-semibold leading-snug text-foreground">
                                    <span className="sr-only">Step {index + 1}: </span>
                                    {step.title}
                                </p>
                                <p className="mt-1 text-sm leading-6 text-muted-foreground">{step.body}</p>
                            </div>
                        </li>
                    ))}
                </ol>

                {/* Rental type cards */}
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    {content.rentalTypes.map((type, index) => (
                        <div
                            key={index}
                            data-testid="rental-type-card"
                            className="flex flex-col rounded-xl border border-border bg-white p-6 transition-[transform,box-shadow,border-color] duration-200 hover:border-primary/20 hover:shadow-[0_18px_34px_rgba(54,49,43,0.08)] motion-safe:hover:-translate-y-1 sm:p-8"
                        >
                            <h3 className="font-display text-2xl leading-tight text-foreground sm:text-3xl">
                                {type.title}
                            </h3>
                            <p className="mt-3 text-sm leading-7 text-muted-foreground sm:text-base">
                                {type.description}
                            </p>
                            <p className="mt-3 text-sm leading-7 text-foreground/70 sm:text-base">
                                {type.detail}
                            </p>
                        </div>
                    ))}
                </div>

            </div>
        </section>
    );
};

export default HowItWorksSection;
